import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Bot, CalendarDays, Inbox, MessageCircle, Search, Sparkles } from 'lucide-react';
import { socket } from './socket';
import ChatListItem from './ChatListItem';
import { formatChatTime } from './chatUtils';

const FILTERS = [
  { id: 'priority', label: 'Prioridad', icon: Sparkles },
  { id: 'unread', label: 'Sin leer', icon: Inbox },
  { id: 'concern', label: 'Preocupados', icon: AlertTriangle },
  { id: 'drafts', label: 'Borradores', icon: Bot },
  { id: 'handoff', label: 'Humano', icon: MessageCircle },
  { id: 'all', label: 'Todos', icon: Search },
];

const DATE_FILTERS = [
  { id: 'today', label: 'Hoy' },
  { id: 'yesterday', label: 'Ayer' },
  { id: 'two_days_ago', label: 'Hace 2 dias' },
  { id: 'all', label: 'Todos' },
];

const PAGE_SIZE = 40;

function ChatList({ userId, onSelect, selectedChatId }) {
  const [chats, setChats] = useState([]);
  const [filter, setFilter] = useState('priority');
  const [dateFilter, setDateFilter] = useState('today');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const sortChats = (items) =>
    [...items].sort((a, b) => {
      if (a.is_handoff_required !== b.is_handoff_required) return a.is_handoff_required ? -1 : 1;
      if (a.has_pending_draft !== b.has_pending_draft) return a.has_pending_draft ? -1 : 1;
      return Date.parse(b.updated_at || 0) - Date.parse(a.updated_at || 0);
    });

  const loadChats = useCallback(async (requestedOffset = 0, reset = false) => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: String(userId),
        filter,
        date_filter: dateFilter,
        limit: String(PAGE_SIZE),
        offset: String(requestedOffset),
      });
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/triage?${params}`);
      if (!res.ok) throw new Error(`triage ${res.status}`);
      const data = await res.json();
      const items = data.items || [];
      setChats(prev => {
        if (reset) return sortChats(items);
        const byId = new Map(prev.map(item => [item.id, item]));
        items.forEach(item => byId.set(item.id, { ...byId.get(item.id), ...item }));
        return sortChats([...byId.values()]);
      });
      setHasMore(Boolean(data.has_more));
      setOffset(data.next_offset ?? requestedOffset + items.length);
    } catch (error) {
      console.error('Error loading triage chats:', error);
      if (reset) setChats([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [userId, filter, dateFilter]);

  useEffect(() => {
    setChats([]);
    setOffset(0);
    setHasMore(true);
    loadChats(0, true);
  }, [loadChats]);

  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) return;
    loadChats(offset, false);
  }, [hasMore, isLoading, loadChats, offset]);

  const handleScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) {
      loadMore();
    }
  };

  useEffect(() => {
    const onChatUpdated = (chat) => {
      setChats(prev => sortChats(prev.map(item => (
        item.id === chat.id ? { ...item, ...chat } : item
      ))));
    };

    const onAIUpdated = (payload) => {
      setChats(prev => sortChats(prev.map(item => {
        if (item.id !== payload.chat_id) return item;
        const settings = payload.settings || {};
        return {
          ...item,
          ai_mode: settings.mode || item.ai_mode,
          is_handoff_required: Boolean(settings.is_handoff_required),
          handoff_reason: settings.handoff_reason,
          last_intent: settings.last_intent,
          last_sentiment: settings.last_sentiment || item.last_sentiment,
          last_confidence: settings.last_confidence,
          has_pending_draft: Boolean(payload.pending_draft),
          pending_draft_at: payload.pending_draft?.created_at || item.pending_draft_at,
        };
      })));
    };

    socket.on('chat_updated', onChatUpdated);
    socket.on('logistics_ai_chat_triage_updated', onAIUpdated);
    return () => {
      socket.off('chat_updated', onChatUpdated);
      socket.off('logistics_ai_chat_triage_updated', onAIUpdated);
    };
  }, []);

  const filtered = chats.filter(chat => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      chat.phone_number?.toLowerCase().includes(q) ||
      chat.last_message?.toLowerCase().includes(q) ||
      chat.last_intent?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-slate-950 font-montserrat">WhatsApp Logistics</h2>
            <p className="text-xs text-slate-500 font-montserrat">Inbox inteligente</p>
          </div>
          <button
            onClick={() => loadChats(0, true)}
            className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200"
          >
            Actualizar
          </button>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 bg-slate-50">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar telefono, mensaje o intent"
            className="w-full bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
          />
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          {FILTERS.map(item => {
            const Icon = item.icon;
            const active = filter === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setFilter(item.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                  active ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="mt-3">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-slate-500 mb-2">
            <CalendarDays className="w-3.5 h-3.5" />
            Fecha
          </div>
          <div className="grid grid-cols-2 gap-2">
            {DATE_FILTERS.map(item => (
              <button
                key={item.id}
                onClick={() => setDateFilter(item.id)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                  dateFilter === item.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <ul className="flex-1 overflow-y-auto" onScroll={handleScroll}>
        {filtered.map(chat => (
          <ChatListItem
            key={chat.id}
            chat={chat}
            isSelected={chat.id === selectedChatId}
            onClick={() => onSelect(chat.id)}
            formatTime={formatChatTime}
          />
        ))}

        <li className="p-4 text-center text-sm text-slate-500 font-montserrat">
          {isLoading
            ? 'Cargando...'
            : filtered.length
              ? hasMore ? `${filtered.length} conversaciones cargadas` : `${filtered.length} conversaciones`
              : 'No hay chats para este filtro'}
        </li>
      </ul>
    </div>
  );
}

export default ChatList;
